import * as React from 'react';
import styled from 'styled-components';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import { useIORef, useUpdateEffect } from '../../core/hooks';
import { useSetter, useGetter, useEffect } from './slots';
import { inputInnerStyle } from './shared';

import { takeCursorData } from './Input';

declare module './slots' {
    interface SelectControls {
        'Simple': 'Simple',
    }
}

interface Props {
    value: Record<string, any> | null,
    children: (x: Record<string, any> | null) => React.ReactNode,
    className?: string,
}

const Component: React.FunctionComponent<Props> = ({ children, value, className }) => {

    const derivedValue = React.useMemo(() => pipe(
        value,
        O.fromNullable,
        O.fold(
            () => ({ text: '' }),
            (s) => s,
        ),
    ), [value]);

    const { read: getValue, write: writeValue } = useIORef<Record<string, any> | null>(derivedValue);

    const [, run] =  useEffect('changeValue');
    const [cursorMove] = useEffect('cursorMove');
    
    const target = useGetter('target');
    const dropdownTarget = useGetter('dropdownTarget');
    const cursor = useGetter('cursor');

    useUpdateEffect(() => {
        const data = dropdownTarget.getMutableData();
        const cursorPosition = cursor.get();

        const value = takeCursorData(target.getValue(), cursorPosition, data);

        change(value);
    }, [cursorMove]);

    const change = React.useCallback(run((value: Record<string, any> | null) => {
        writeValue(value)();
    }), []);

    const isEmpty = () => {
        const x = getValue();

        return x ? x.text === '' : x === null;
    };

    const reset = React.useCallback(() => {
        change({ text: '' });
    }, []);

    useSetter('target', {
        tag: 'Simple',
        getValue,
        change,
        isEmpty,
        reset,
    });


    React.useEffect(() => {
        change(derivedValue);
    }, [derivedValue]);


    return (
        <Simple className={className}>{children(getValue())}</Simple>
    );
}

const Simple = styled.span`
    ${inputInnerStyle}
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    display: -webkit-box;
    align-self: center;
`;

export default Component;
